import { getBffBaseUrl, getVisitKey, LANGUAGES_PATH } from '@/lib/bff/config'

/** A language available on a tenant, as returned by the BFF. */
export type Language = {
  /** BCP 47 language tag, e.g. "en-GB", "cy", "fr-FR". */
  code: string
  /** Human-readable language name for display. */
  name: string
}

/** Raised when the BFF rejects the access token (HTTP 401). */
export class LanguageAuthError extends Error {
  constructor(message = 'Not authorised for language access') {
    super(message)
    this.name = 'LanguageAuthError'
  }
}

/** Raised for any other failure (network, timeout, 5xx, malformed body). */
export class LanguageRequestError extends Error {
  constructor(message = 'Could not resolve tenant languages') {
    super(message)
    this.name = 'LanguageRequestError'
  }
}

function normalise(data: unknown): Language[] {
  if (!Array.isArray(data)) throw new LanguageRequestError('Unexpected language response shape')
  return data.map((entry) => {
    if (
      !entry ||
      typeof entry !== 'object' ||
      typeof (entry as Language).code !== 'string' ||
      typeof (entry as Language).name !== 'string'
    ) {
      throw new LanguageRequestError('Unexpected language response shape')
    }
    const { code, name } = entry as Language
    return { code, name }
  })
}

/*
 * Resolve the languages available on a tenant. The BFF is the source of truth
 * for tenant language availability: the Auth0 access token is sent as a Bearer
 * credential, the environment-scoped x-visit-key as a header, and the active
 * tenant id as a query parameter so the BFF returns only that tenant's
 * languages. The URL is built from the configured BFF base URL, falling back to
 * the current host when none is configured.
 */
export async function fetchLanguages(
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<Language[]> {
  const url = `${getBffBaseUrl('mmv')}${LANGUAGES_PATH}?tenant=${encodeURIComponent(tenantId)}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-visit-key': getVisitKey(),
        Accept: 'application/json',
      },
      signal,
    })
  } catch {
    throw new LanguageRequestError('Network error while resolving languages')
  }

  if (response.status === 401) throw new LanguageAuthError()
  if (!response.ok) throw new LanguageRequestError(`Language resolution failed (${response.status})`)

  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new LanguageRequestError('Malformed language response')
  }

  return normalise(data)
}
